/**
 * Deleting the project rows behind one sidebar row.
 *
 * Split out of `sidebarProjectRemoval.ts` when it went past the file cap. What
 * lives here is the bounded, per-member deletion; what stays behind decides
 * whether any of it should run at all.
 *
 * The reason the two ref lists are gathered separately is the point of the
 * file: local members and runtime-hosted members are treated differently, and
 * only the local ones may fail a removal.
 */
import type { ScopedProjectRef } from "@cmmd-forge/contracts";
import { scopeProjectRef, scopedProjectKey } from "@cmmd-forge/client-runtime";
import { readEnvironmentApi } from "../../environmentApi";
import { classifyEnvironmentId } from "../../environments/environmentIdClassification";
import { useComposerDraftStore } from "../../composerDraftStore";
import { newCommandId } from "../../lib/utils";
import { REMOVAL_STEP_LABELS } from "./removalStepLabels";
import type { RemovableForgeProject } from "./sidebarProjectRemoval";
import {
  destroyCmmdRuntimeForEnvironment,
  isProjectAlreadyGoneError,
  settleRemovalStep,
  type RemovalStepOutcome,
} from "./sidebarProjectRemovalSteps";

function dedupeProjectRefs(refs: readonly ScopedProjectRef[]): ScopedProjectRef[] {
  const seen = new Set<string>();
  const out: ScopedProjectRef[] = [];
  for (const ref of refs) {
    const key = scopedProjectKey(ref);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(ref);
  }
  return out;
}

export function resolveLocalProjectMemberRefs(
  project: RemovableForgeProject,
): readonly ScopedProjectRef[] {
  const representative =
    !project.preConnectCloud && !classifyEnvironmentId(project.environmentId).isCloud
      ? [scopeProjectRef(project.environmentId, project.id)]
      : [];
  return dedupeProjectRefs([
    ...representative,
    ...project.memberProjectRefs.filter(
      (memberRef) => !classifyEnvironmentId(memberRef.environmentId).isCloud,
    ),
    ...(project.worktreeMembers ?? [])
      .filter((member) => member.environmentRole === "local")
      .map((member) => member.projectRef),
  ]);
}

/**
 * The members that live on a Forge Cloud runtime.
 *
 * Counterpart to {@link resolveLocalProjectMemberRefs}. Between them they cover
 * every member of the row and neither claims one twice, so a member is deleted
 * exactly once and against the right authority.
 *
 * Keyed on whether the id names Forge Cloud rather than on a role string,
 * because a member on someone else's desktop runtime is remote but is not ours
 * to delete when a Forge Cloud registration goes away.
 *
 * `isCloud`, not the legacy runtime id: a durable `env_` Environment names no
 * runtime in its id and is hosted on Forge Cloud all the same. Sorting it into
 * the local bucket made its cleanup mandatory, so a durable row whose Sprite
 * the unregister had just ended reported a completed removal as a failure and
 * skipped the sidebar refresh — with the registration and the files already
 * gone.
 */
export function resolveRuntimeHostedProjectMemberRefs(
  project: RemovableForgeProject,
): readonly ScopedProjectRef[] {
  // A pre-connect row's own id is a sentinel, not a project the runtime knows.
  // Its real members arrive as refs once the machine connects.
  const representative =
    !project.preConnectCloud && classifyEnvironmentId(project.environmentId).isCloud
      ? [scopeProjectRef(project.environmentId, project.id)]
      : [];
  return dedupeProjectRefs([
    ...representative,
    ...project.memberProjectRefs.filter(
      (memberRef) => classifyEnvironmentId(memberRef.environmentId).isCloud,
    ),
    ...(project.worktreeMembers ?? [])
      .filter((member) => member.environmentRole === "cloud")
      .map((member) => member.projectRef),
  ]);
}

export async function removeProjectMembers(
  memberRefs: readonly ScopedProjectRef[],
  options: { readonly destroyRuntime: boolean },
): Promise<{ readonly removed: boolean; readonly errors: readonly unknown[] }> {
  const errors: unknown[] = [];
  const draftStore = useComposerDraftStore.getState();
  const memberOutcomes = await Promise.all(
    memberRefs.map(async (memberRef) => {
      const draft = draftStore.getDraftThreadByProjectRef(memberRef);
      if (draft) draftStore.clearDraftThread(draft.draftId);
      draftStore.clearProjectDraftThreadId(memberRef);

      const projectApi = readEnvironmentApi(memberRef.environmentId);
      const [teardown, memberDeletion] = await Promise.all([
        options.destroyRuntime
          ? settleRemovalStep(REMOVAL_STEP_LABELS.destroyRuntime, (signal) =>
              destroyCmmdRuntimeForEnvironment(memberRef.environmentId, {
                signal,
              }),
            )
          : Promise.resolve<RemovalStepOutcome<void>>({
              ok: true,
              value: undefined,
            }),
        projectApi
          ? settleRemovalStep(REMOVAL_STEP_LABELS.deleteForgeProject, () =>
              // `dispatchCommand` takes no AbortSignal, so the timeout unblocks
              // the caller without cancelling the in-flight RPC.
              projectApi.orchestration.dispatchCommand({
                type: "project.delete",
                commandId: newCommandId(),
                projectId: memberRef.projectId,
              }),
            )
          : Promise.resolve<RemovalStepOutcome<never>>({
              ok: false,
              error: new Error("Project API unavailable."),
            }),
      ]);
      return { teardown, memberDeletion };
    }),
  );

  let everyMemberDeleted = true;
  for (const { teardown, memberDeletion } of memberOutcomes) {
    if (!teardown.ok) errors.push(teardown.error);
    if (memberDeletion.ok || isProjectAlreadyGoneError(memberDeletion.error)) continue;
    errors.push(memberDeletion.error);
    everyMemberDeleted = false;
  }

  return { removed: everyMemberDeleted, errors };
}
